import { useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
  CheckIcon,
  ArrowsRightLeftIcon,
  PencilSquareIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import img from "../assets/images/logo.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppContext, hasMultipleProgrammes } from "../functions/ContextAPI";

const navigation = {
  items: [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: HomeIcon,
      description: "View your dashboard and programme overview"
    },
    {
      name: "Programme Selection",
      href: "/settings",
      icon: AcademicCapIcon,
      description: "Select or switch between programmes"
    },
    {
      name: "Score Submission",
      href: "/results",
      icon: PencilSquareIcon,
      description: "View accreditation results and submissions"
    },
  ],
  documentsMenu: [
    {
      name: "NUC Documents",
      href: "/documents/nuc",
      icon: DocumentArrowDownIcon,
      description: "Official NUC accreditation forms"
    },
    {
      name: "DAP Documents",
      href: "/documents/dap",
      icon: ClipboardDocumentListIcon,
      description: "Institution uploaded documents"
    },
    {
      name: "Upload Documents",
      href: "/documents/upload",
      icon: DocumentArrowUpIcon,
      description: "Upload programme documents"
    },
  ],
  signout: {
    name: "Sign Out",
    href: "/signout",
    icon: ArrowRightOnRectangleIcon
  }
};

const NavigationBar = () => {
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const context = useAppContext();

  // Get current programme and programmes list
  const currentProgramme = context.selectedProgramme || context.programmes?.[0];
  const showProgrammeSwitcher = hasMultipleProgrammes(context);

  // Get user data
  const user = context.user || { name: "Representative User", email: "rep@nuc.edu.ng", initials: "RU" };

  // Handle programme switching
  const handleProgrammeSwitch = (programme) => {
    context.setSelectedProgramme(programme);

    // Redirect to dashboard with new programme
    navigate(`/dashboard`);
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Generate breadcrumbs from current path
  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const breadcrumbs = [
      { name: 'Dashboard', href: '/dashboard', current: pathnames.length === 0 }
    ];

    pathnames.forEach((name, index) => {
      const href = `/${pathnames.slice(0, index + 1).join('/')}`;
      const isLast = index === pathnames.length - 1;
      breadcrumbs.push({
        name: name.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        href,
        current: isLast
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <>
      {/* Skip Link for Accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary-600 text-white px-4 py-2 rounded-lg z-50"
      >
        Skip to main content
      </a>

      {/* Enhanced Navigation Bar */}
      <div className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-md shadow-md border-b border-gray-200'
          : 'bg-white shadow-sm border-b border-gray-100'
      }`}>

        {/* Enhanced Mobile Menu */}
        <Dialog className="relative z-50 lg:hidden" open={open} onClose={() => setOpen(false)}>
          <Transition
            show={open}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
          </Transition>

          <div className="fixed inset-0 z-50 flex">
            <Transition
              show={open}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative flex w-full max-w-xs flex-col overflow-y-auto bg-white pb-12 shadow-2xl">
                {/* Mobile menu header */}
                <div className="flex items-center justify-between px-4 pb-2 pt-5 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <img className="h-8 w-auto" src={img} alt="NUC Logo" />
                    <span className="text-lg font-bold text-primary-600">
                      NUC
                    </span>
                  </div>
                  <button
                    type="button"
                    className="relative -m-2 inline-flex items-center justify-center rounded-xl p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-all duration-200"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* User profile section in mobile */}
                <div className="px-4 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center text-white font-medium">
                      {user.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">Representative</p>
                    </div>
                  </div>
                </div>

                {/* Mobile programme switcher */}
                {showProgrammeSwitcher && currentProgramme && (
                  <div className="px-4 py-4 border-b border-gray-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Current Programme</p>
                    <div className="bg-primary-50 rounded-lg p-3">
                      <div className="flex items-start space-x-3">
                        <AcademicCapIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-primary-900 truncate">
                            {currentProgramme.programmeName}
                          </div>
                          <div className="text-xs text-primary-700 truncate">
                            {currentProgramme.institutionName}
                          </div>
                        </div>
                      </div>
                    </div>
                    {context.programmes?.length > 1 && (
                      <Link
                        to="/programme-selection"
                        className="mt-3 inline-flex items-center text-xs text-primary-600 hover:text-primary-700 font-medium"
                        onClick={() => setOpen(false)}
                      >
                        <ArrowsRightLeftIcon className="h-3 w-3 mr-1" />
                        Switch Programme
                      </Link>
                    )}
                  </div>
                )}

                {/* Mobile navigation items */}
                <div className="space-y-1 px-4 py-6">
                  {navigation.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                    return (
                      <div key={item.name} className="flow-root">
                        <Link
                          to={item.href}
                          className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                            isActive
                              ? 'bg-primary-50 text-primary-700 shadow-sm'
                              : 'text-gray-900 hover:bg-primary-50 hover:text-primary-700'
                          }`}
                          onClick={() => setOpen(false)}
                        >
                          <Icon className={`h-5 w-5 mr-3 transition-colors duration-200 ${
                            isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-primary-600'
                          }`} />
                          <div>
                            <div>{item.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                          </div>
                        </Link>
                      </div>
                    );
                  })}

                  {/* Mobile Documents Menu */}
                  <div className="pt-4 pb-2 border-t border-gray-200 mt-4">
                    <p className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Documents
                    </p>
                    {navigation.documentsMenu.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <div key={item.name} className="flow-root">
                          <Link
                            to={item.href}
                            className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                              isActive
                                ? 'bg-primary-50 text-primary-700 shadow-sm'
                                : 'text-gray-900 hover:bg-primary-50 hover:text-primary-700'
                            }`}
                            onClick={() => setOpen(false)}
                          >
                            <Icon className={`h-5 w-5 mr-3 transition-colors duration-200 ${
                              isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-primary-600'
                            }`} />
                            <div>
                              <div>{item.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                            </div>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Mobile sign out */}
                <div className="border-t border-gray-200 px-4 py-4 mt-auto">
                  <Link
                    to={navigation.signout.href}
                    className="group flex items-center px-4 py-3 text-sm font-medium text-red-600 rounded-xl hover:bg-red-50 transition-all duration-200"
                    onClick={() => setOpen(false)}
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                    {navigation.signout.name}
                  </Link>
                </div>
              </DialogPanel>
            </Transition>
          </div>
        </Dialog>

        {/* Main Header */}
        <header className="relative">
          <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">

              {/* Logo and Brand */}
              <div className="flex items-center">
                <Link to="/dashboard" className="flex items-center space-x-3 group">
                  <div className="relative">
                    <img className="h-10 w-auto transition-transform duration-200 group-hover:scale-105" src={img} alt="NUC Logo" />
                  </div>
                  <div className="hidden sm:block">
                    <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
                      NUC REPRESENTATIVE
                    </span>
                    <div className="text-xs text-gray-500 font-normal -mt-1">Accreditation Portal</div>
                  </div>
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex lg:items-center lg:space-x-1">
                {navigation.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 relative ${
                        isActive
                          ? 'text-primary-700 bg-primary-50 shadow-sm'
                          : 'text-gray-700 hover:text-primary-700 hover:bg-primary-50'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon className={`h-4 w-4 mr-2 transition-colors duration-200 ${
                        isActive ? 'text-primary-600' : 'text-gray-400 hover:text-primary-600'
                      }`} />
                      {item.name}
                      {isActive && (
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 w-8 bg-primary-600 rounded-full"></div>
                      )}
                    </Link>
                  );
                })}

                {/* Desktop Documents Dropdown */}
                <Menu as="div" className="relative">
                  <MenuButton className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 relative ${
                    location.pathname.startsWith('/documents')
                      ? 'text-primary-700 bg-primary-50 shadow-sm'
                      : 'text-gray-700 hover:text-primary-700 hover:bg-primary-50'
                  }`}>
                    <ClipboardDocumentListIcon className={`h-4 w-4 mr-2 transition-colors duration-200 ${
                      location.pathname.startsWith('/documents') ? 'text-primary-600' : 'text-gray-400 hover:text-primary-600'
                    }`} />
                    Documents
                    <ChevronDownIcon className="h-4 w-4 ml-1 text-gray-400" />
                    {location.pathname.startsWith('/documents') && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 w-8 bg-primary-600 rounded-full"></div>
                    )}
                  </MenuButton>

                  <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <MenuItems className="absolute right-0 z-20 mt-2 w-72 origin-top-right bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">Document Management</p>
                        <p className="text-xs text-gray-500 mt-1">Select document type to view or manage</p>
                      </div>

                      <div className="py-1">
                        {navigation.documentsMenu.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.href;
                          return (
                            <MenuItem key={item.name}>
                              {({ focus }) => (
                                <Link
                                  to={item.href}
                                  className={`block px-4 py-3 text-sm transition-colors duration-200 ${
                                    focus ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                                  }`}
                                >
                                  <div className="flex items-start space-x-3">
                                    <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                                      isActive ? 'text-primary-600' : 'text-gray-400'
                                    }`} />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium">{item.name}</div>
                                      <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                                    </div>
                                  </div>
                                </Link>
                              )}
                            </MenuItem>
                          );
                        })}
                      </div>
                    </MenuItems>
                  </Transition>
                </Menu>
              </div>

              {/* Programme Switcher */}
              {showProgrammeSwitcher && currentProgramme && (
                <div className="hidden lg:block">
                  <Menu as="div" className="relative">
                    <MenuButton className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-all duration-200 group">
                      <AcademicCapIcon className="h-4 w-4 text-gray-400 group-hover:text-primary-600 transition-colors duration-200" />
                      <div className="text-left">
                        <div className="text-sm font-medium truncate max-w-32">
                          {currentProgramme.programmeName}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-32">
                          {currentProgramme.institutionName}
                        </div>
                      </div>
                      <ChevronDownIcon className="h-4 w-4 text-gray-400 group-hover:text-primary-600 transition-colors duration-200" />
                    </MenuButton>

                    <Transition
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="absolute right-0 z-20 mt-2 w-80 origin-top-right bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <div className="flex items-center space-x-2">
                            <ArrowsRightLeftIcon className="h-4 w-4 text-primary-600" />
                            <p className="text-sm font-medium text-gray-900">Switch Programme</p>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Select a different programme to work on</p>
                        </div>

                        <div className="py-1 max-h-64 overflow-y-auto">
                          {context.programmes?.map((programme) => (
                            <MenuItem key={programme.id}>
                              {({ focus }) => (
                                <button
                                  onClick={() => handleProgrammeSwitch(programme)}
                                  className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 ${
                                    focus ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium truncate">
                                        {programme.programmeName}
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">
                                        {programme.institutionName}
                                      </div>
                                    </div>
                                    {currentProgramme?.id === programme.id && (
                                      <CheckIcon className="h-4 w-4 text-primary-600 flex-shrink-0 ml-2" />
                                    )}
                                  </div>
                                </button>
                              )}
                            </MenuItem>
                          ))}
                        </div>
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>
              )}

              {/* Right side actions */}
              <div className="flex items-center space-x-3">

                {/* User Info and Logout Button */}
                <div className="hidden lg:flex items-center space-x-4">
                  {/* Logout Button */}
                  <Link
                    to={navigation.signout.href}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-all duration-200 group"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    <span>Logout</span>
                  </Link>
                </div>

                {/* Mobile menu button */}
                <button
                  type="button"
                  className="relative rounded-lg bg-white p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 lg:hidden transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  onClick={() => setOpen(true)}
                  aria-expanded={open}
                  aria-label="Toggle navigation menu"
                >
                  <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </nav>
        </header>

        {/* Breadcrumb Navigation */}
        {breadcrumbs.length > 1 && (
          <div className="border-t border-gray-100 bg-gray-50">
            <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2 py-3 text-sm">
                {breadcrumbs.map((breadcrumb, index) => (
                  <li key={breadcrumb.href} className="flex items-center">
                    {index > 0 && (
                      <ChevronDownIcon className="h-4 w-4 text-gray-400 mx-2 rotate-[-90deg]" />
                    )}
                    {breadcrumb.current ? (
                      <span className="font-medium text-primary-600">{breadcrumb.name}</span>
                    ) : (
                      <Link
                        to={breadcrumb.href}
                        className="font-medium text-gray-500 hover:text-primary-600 transition-colors duration-200"
                      >
                        {breadcrumb.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        )}
      </div>
    </>
  );
};

export default NavigationBar;